
import moment from 'moment';
import {
  DISPLAYMODE_PARK,
  DISPLAYMODE_RENTALS,
  DISPLAYMODE_OTHER,
} from '../reducers/layers.js';

export const vehiclesAbortController = new AbortController();

/**
 * zone_id of the country-level "Nederland" zone. Used to scope per-zone
 * aggregated stats to a single non-overlapping zone for NL-wide accounts.
 */
export const NL_COUNTRY_ZONE_ID = 51233;

/**
 * Unique zone_ids of the 'municipality' zones in `zones`. When
 * `municipalityCodes` is given, only zones of those gm_codes are returned.
 */
export const getMunicipalityZoneIds = (zones, municipalityCodes) => {
  const zoneIds = (zones || [])
    .filter((zone) => (
      zone.zone_type === 'municipality'
      && (!municipalityCodes || municipalityCodes.indexOf(zone.municipality) > -1)
    ))
    .map((zone) => zone.zone_id);
  return [...new Set(zoneIds)];
};

/** system_ids from /menu/acl (operator accounts have a restricted list). */
export const getAclOperatorSystemIds = (metadata) => {
  const aclOperators = metadata?.aclOperators || [];
  return aclOperators
    .map((operator) => operator.system_id)
    .filter(Boolean);
};

/** Append `operators=` when the logged-in user is scoped to specific operators. */
export const appendAclOperatorsToUrl = (url, metadata) => {
  const systemIds = getAclOperatorSystemIds(metadata);
  if (systemIds.length === 0) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}operators=${systemIds.join(',')}`;
};

export const createFilterparameters = (displayMode, filter, metadata, options) => {
  const isParkingData = displayMode === DISPLAYMODE_PARK;
  const isRentalData = displayMode === DISPLAYMODE_RENTALS;
  const isOntwikkelingData = displayMode === DISPLAYMODE_OTHER;

  options = options || {
    includeOperators: false
  }

  const hasAccessToFilterGebied = metadata.gebieden && metadata.gebieden.filter(gebied => gebied.gm_code === filter.gebied).length >= 1;
  const hasAccessToMultipleGebieden = metadata.gebieden && metadata.gebieden.length >= 1;
  const municipalityCodesAsArray = metadata.gebieden.map(x => x.gm_code)

  // ADD ZONES
  let filterparams = [];
  // If zones are explicity asked: add these to the request query
  if (filter.zones !== "") {
    filterparams.push("zone_ids=" + filter.zones);
  }
  // If a place is selected, get all zones for this place
  else if (filter.gebied !== "" && hasAccessToFilterGebied) {
    const municipality = metadata.gebieden.find(gebied => gebied.gm_code === filter.gebied);
    let candidates = [];

    // Prefer an exact name match on a non-custom zone (the municipality's own
    // boundary). This is what existed historically and works for places that
    // have exactly one zone with the same name as the municipality.
    if (municipality !== undefined) {
      candidates = metadata.zones.filter(zone => {
        return zone.municipality === municipality.gm_code
          && zone.name === municipality.name
          && zone.zone_type !== 'custom';
      });
    }

    // Fallback 1: zones explicitly typed 'municipality' for this gm_code.
    if (candidates.length === 0) {
      candidates = metadata.zones.filter(zone => {
        return zone.municipality === filter.gebied && zone.zone_type === 'municipality';
      });
    }

    // Fallback 2: any non-custom zone for this gm_code. Keeps requests scoped
    // to the selected plaats instead of falling through to NL-wide data.
    if (candidates.length === 0) {
      candidates = metadata.zones.filter(zone => {
        return zone.municipality === filter.gebied && zone.zone_type !== 'custom';
      });
    }

    if (candidates.length >= 1) {
      const zoneIds = [...new Set(candidates.map(zone => zone.zone_id))];
      filterparams.push("zone_ids=" + zoneIds.join(','));
    }
  }
  // Logged-in users with no specific place selected ("Alle plaatsen"), for
  // per-zone aggregated endpoints (aggregated_stats/*, stats_v2/*). These
  // endpoints sum the stats of every zone matched, and without zone_ids the
  // backend matches all overlapping layers (country + municipality + stadsdeel
  // + wijk), so every count comes out 4x too high. Always send an explicit,
  // non-overlapping zone set here:
  // - Admin / NL-wide accounts (metadata.is_admin, set from the ACL): the
  //   single country zone. Enumerating every municipality would make the query
  //   string long enough that the upstream server rejects it with a 502; if the
  //   country zone yields nothing, getAggregatedStats falls back to batched
  //   municipality requests.
  // - Other accounts: the 'municipality' zones of the gebieden they may access.
  //   Long lists are split into batches by getAggregatedStats.
  else if (options.is_logged_in && options.is_aggregated_stats && !options.show_global) {
    if (metadata.is_admin === true) {
      filterparams.push(`zone_ids=${NL_COUNTRY_ZONE_ID}`);
    } else {
      const zoneIds = getMunicipalityZoneIds(metadata.zones, municipalityCodesAsArray);
      // Zones may not have loaded yet; callers wait for metadata.zones before
      // fetching, so this only guards against an empty `zone_ids=` param.
      if (zoneIds.length > 0) {
        filterparams.push(`zone_ids=${zoneIds.join(',')}`);
      }
    }
  }
  // Logged-in users with no specific place selected ("Alle plaatsen"), for
  // point endpoints (park_events, trips): the backend already scopes the
  // response to the account's allowed data via the auth token, so we
  // intentionally add no zone_ids. This is also required for accounts with
  // access to a large number of municipalities (e.g. NL-wide shared data):
  // enumerating every municipality/zone would make the query string long
  // enough that the upstream server rejects it with a 502.
  else if (options.is_logged_in && !options.show_global) {
    // No zone_ids: the auth token (+ operators=) scopes the data server-side.
  }
  // Guests with access to multiple (public) municipalities: scope to those zones
  // when zone metadata is available. When zones have not loaded yet (typical for
  // guests with gebied === ""), omit zone_ids so the request stays NL-wide
  // instead of sending an ambiguous empty `zone_ids=` param (issue #259).
  else if (hasAccessToMultipleGebieden && !options.show_global) {
    const zoneIds = getMunicipalityZoneIds(metadata.zones, municipalityCodesAsArray);
    if (zoneIds.length > 0) {
      filterparams.push(`zone_ids=${zoneIds.join(',')}`);
    }
  }
  // If no place is set: Get NL data (NL 'zone')
  // Only providers and admins are allowed to see this info
  else {
    // Nevermind: we don't filter on country, we show everything
    // filterparams.push("zone_ids=51233");
  }

  const hasOperatorsParam = () => filterparams.some((p) => p.startsWith('operators='));

  // system_ids from the public NL-wide /operators API (state.metadata.aanbieders).
  const getPublicOperatorSystemIds = () => {
    return (metadata?.aanbieders || [])
      .map((operator) => operator.system_id || operator.value)
      .filter(Boolean);
  };

  // Operator-type accounts (single operator in /menu/acl) must always be
  // scoped to their own operator. For municipality / admin accounts we want
  // the request to reflect the full NL-wide /operators list so newly added
  // operators (e.g. 'voi') are included even when /menu/acl lags behind.
  // The backend enforces data ACL based on the auth token + zone_ids.
  const appendOperatorsScope = () => {
    if (hasOperatorsParam()) {
      return;
    }
    const aclSystemIds = getAclOperatorSystemIds(metadata);
    if (aclSystemIds.length === 1) {
      filterparams.push('operators=' + aclSystemIds.join(','));
      return;
    }
    const publicSystemIds = getPublicOperatorSystemIds();
    if (publicSystemIds.length > 0) {
      filterparams.push('operators=' + publicSystemIds.join(','));
    }
  };

  if (options.includeOperators === true) {
    // Add provider filter
    if (filter.aanbiedersexclude !== "" && filter.aanbiedersexclude !== undefined) {
      let filteritems = filter.aanbiedersexclude.split(",");
      let selectedaanbieders = metadata.aanbieders
        .filter(aanbieder => (filteritems.includes(aanbieder.system_id) === false))
        .map(aanbieder => aanbieder.system_id).join(",");

      filterparams.push("operators=" + selectedaanbieders);
    } else {
      appendOperatorsScope();
    }
  } else {
    // filtering is done client side
  }

  // For operator-type accounts the request must always be scoped to their own
  // operator, even when filtering is done client-side. Other accounts get the
  // full public operators list (see appendOperatorsScope).
  appendOperatorsScope();

  // Add vehicle type filter
  // form_factors=[cargo_bicycle,moped,bicycle,car,other]
  if (filter.voertuigtypesexclude !== '' && filter.voertuigtypesexclude !== undefined) {
    let filteritems = filter.voertuigtypesexclude.split(",");
    let selectedtypes = metadata.vehicle_types
      .filter(vtype => (filteritems.includes(vtype.id) === false))
      .map(vtype => vtype.id).join(",");

    filterparams.push("form_factors=" + selectedtypes);
  }

  // Add date (start and/or end)
  if (isParkingData) {
    let ts = new Date().toISOString().replace(/.\d+Z$/g, "Z"); // use current time without decimals
    if (filter.datum) {
      ts = new Date(filter.datum).toISOString().replace(/.\d+Z$/g, "Z");
    }
    filterparams.push("timestamp=" + ts)
  }

  if (isRentalData) {
    let ts1 = new Date().toISOString().replace(/.\d+Z$/g, "Z"); // use current time without decimals
    let ts2 = ts1;
    // filter.intervalstart = moment().subtract(filter.intervalduur / 1000, 'seconds').toISOString();
    // filter.intervalend = moment().toISOString();
    const isInvalid = () => {
      return !filter
        // || filter.intervalstart === ""
        || filter.intervalend === ""
        // || filter.intervalstart === undefined
        || filter.intervalend === undefined;
    }
    if (!isInvalid()) {
      ts1 = moment(filter.intervalend).subtract(filter.intervalduur / 1000, 'seconds').toISOString().replace(/.\d+Z$/g, "Z");
      ts2 = moment(filter.intervalend).toISOString().replace(/.\d+Z$/g, "Z");
    }
    filterparams.push("start_time=" + ts1 + "&end_time=" + ts2)
  }

  if (isOntwikkelingData) {
    let van = undefined;
    let tot = undefined;
    if (filter.ontwikkelingvan && filter.ontwikkelingtot) {
      van = new Date(filter.ontwikkelingvan);
      tot = new Date(moment(filter.ontwikkelingtot).add(1, 'day'));
    }

    if (!van || !tot) {
      van = new Date();
      // take now, strip hours, add 24 h
      tot = new Date((new Date()).toDateString());
      van.setDate(tot.getDate() - 7); // go back 1 week
    }

    // toISOString(true) keeps local timezone://momentjs.com/docs/#/displaying/as-iso-string/
    // Date format to create: 2020-12-31T23:00:00Z
    // let ts1 = van.toISOString().replace(/.\d+Z$/g, "Z"); // use current time without decimals
    // let ts2 = tot.toISOString().replace(/.\d+Z$/g, "Z"); // use current time without decimals
    let ts1 = moment(van).format('YYYY-MM-DDTHH:mm:ss') + 'Z'; // use current time without decimals
    let ts2 = moment(tot).format('YYYY-MM-DDTHH:mm:ss') + 'Z'; // use current time without decimals
    filterparams.push("start_time=" + ts1 + "&end_time=" + ts2)
  }

  return filterparams;
}

const NEAR_REALTIME_MARGIN_MS = 10 * 60 * 1000;

/**
 * Whether the selected date/time (filter.datum) is (near) realtime: within 10
 * minutes of now. An unset datum means "now".
 */
export const isNearRealtime = (datum, now = Date.now()) => {
  if (!datum) return true;
  const datumMs = Date.parse(datum);
  if (isNaN(datumMs)) return false;
  return Math.abs(now - datumMs) <= NEAR_REALTIME_MARGIN_MS;
};

export const convertDurationToBin = (duration) => {
  if (duration < 60 * 24 * 2) {
    return 0;
  }
  else if (duration < 60 * 24 * 4) {
    return 1;
  }
  else if (duration < 60 * 24 * 7) {
    return 2;
  }
  else if (duration < 60 * 24 * 14) {
    return 3;
  }
  return 4;
}

export const convertDistanceToBin = (distance_in_meters) => {
  if (distance_in_meters <= 1000) {
    return 0;
  }
  if (distance_in_meters <= 2000) {
    return 1;
  }
  if (distance_in_meters <= 5000) {
    return 2;
  }
  return 3;
}

// abortableFetch
// Source: https://davidwalsh.name/cancel-fetch
export function abortableFetch(request, opts) {
  const controller = new AbortController();
  const signal = controller.signal;

  return {
    abort: () => controller.abort(),
    ready: fetch(request, { ...opts, signal })
  };
}

// Do we have to (re)fetch vehicles via the API?
export const shouldFetchVehicles = (newFilter, existingFilter) => {

  // If no filter was known of: trigger fetch
  if (!existingFilter) return true;

  // If one of these fields change, we should (re)fetch
  const fieldChangesThatShouldTriggerUpdate = [
    'datum',
    'gebied',
    'herkomstbestemming',
    'intervalend',//Eindtijd
    'intervalduur',
    'ontwikkelingaggregatie',
    'ontwikkelingtot',
    'ontwikkelingvan',
    'show_all_nl_vehicles',
    'voertuigtypesexclude',
    'zones',
  ];

  let doFetchVehicles = false;
  fieldChangesThatShouldTriggerUpdate.forEach(x => {
    // If field was changed: (re)fetch
    if (newFilter[x] !== existingFilter[x]) {
      doFetchVehicles = true;
    }
  })

  return doFetchVehicles;
}

// Do we have to (re)fetch vehicles via the API?
export const shouldFetchVehicleStats = (newFilter, existingFilter) => {

  // If no filter was known of: trigger fetch
  if (!existingFilter) return true;

  // If one of these fields change, we should (re)fetch
  const fieldChangesThatShouldTriggerUpdate = [
    'datum',
    'gebied',
    'herkomstbestemming',
    'voertuigtypesexclude',
    'zones',
  ];

  let doFetchVehicles = false;
  fieldChangesThatShouldTriggerUpdate.forEach(x => {
    // If field was changed: (re)fetch
    if (newFilter[x] !== existingFilter[x]) {
      doFetchVehicles = true;
    }
  })

  return doFetchVehicles;
}
